/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useEffect } from 'react';
import DataHookMock from '../../components/Basic/Provider/context/DataHookMock.js';
import getKey from '../utils/getKey.js';
import validateParams from '../utils/validateParams.js';
import { indexInterpolate } from '../utils/indexInterpolate.js';
import useStore, { palDataHookStore } from '../useStore/useStore.js';
import modify from '../modify/modify.js';
import retrieve from '../retrieve/retrieve.js';

var useDataHook = (function (schema) {
  var name = schema.name,
    type = schema.type,
    uriTemplate = schema.uri,
    method = schema.method,
    headers = schema.headers,
    _schema$enableImsAcco = schema.enableImsAccountHeader,
    enableImsAccountHeader = _schema$enableImsAcco === void 0 ? true : _schema$enableImsAcco,
    _schema$enableBssAcco = schema.enableBssAccountHeader,
    enableBssAccountHeader = _schema$enableBssAcco === void 0 ? true : _schema$enableBssAcco,
    graphql = schema.graphql,
    mock = schema.mock,
    _schema$documentation = schema.documentation,
    documentation = _schema$documentation === void 0 ? {} : _schema$documentation,
    dataHelper = schema.dataHelper;
  var _documentation$params = documentation.params,
    acceptedParams = _documentation$params === void 0 ? null : _documentation$params;
  var useDataHook = function useDataHook() {
    var _DataHookMock$useData;
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var shouldMockDataHook = (DataHookMock === null || DataHookMock === void 0 || (_DataHookMock$useData = DataHookMock.useDataHookMock) === null || _DataHookMock$useData === void 0 ? void 0 : _DataHookMock$useData.call(DataHookMock)) || false; // Check if the datahook context is trying to mock
    var params = props.params,
      _props$options = props.options,
      options = _props$options === void 0 ? {} : _props$options;
    /* Check the provided params to the accepted params. This is a soft error, if they don't match the code will still
    execute, but console errors will be printed */
    if (!options.skip) {
      validateParams(acceptedParams, params, name);
    }

    // Every data hook takes in two parameters, params and options

    // Use a provided key, or generate a new key based on the schema, params, and options
    var _options$key = options.key,
      key = _options$key === void 0 ? getKey({
        name: name,
        params: params,
        options: options
      }) : _options$key;

    // Interpolates option based variables into the URI
    var uri = indexInterpolate(uriTemplate, options.templateKeys);

    // This sets up our data store or hooks into an existing one. Data stores require a name and key, this is how the data is scoped.
    var _useStore = useStore({
        key: key // useDataStore returns the state and a function to set the state
      }),
      _useStore2 = _slicedToArray(_useStore, 2),
      _useStore2$ = _useStore2[0],
      response = _useStore2$.response,
      listeners = _useStore2$.listeners,
      setData = _useStore2[1];

    // Watch for makeRequest change. This will trigger a new retrieve request if set to true
    var makeRequest = listeners.makeRequest;

    // useEffect keeps us up to date as state changes. If we change any params or options, or if the data changes then we will update our data
    useEffect(function () {
      retrieve({
        key: key,
        // Current state
        setData: setData,
        // setState function
        params: params,
        // Provided Params
        options: _objectSpread2(_objectSpread2({}, options), {}, {
          mock: shouldMockDataHook || options.mock
        }),
        // Provided options
        method: method,
        // Function to call to get data
        mock: mock,
        // Mock response on return
        uri: uri,
        // URL to send the fetch request to
        headers: headers,
        // Any headers needed for the API call. content type JSON is set by default
        enableImsAccountHeader: enableImsAccountHeader,
        // Include X-IMS-Account header in requests or not
        enableBssAccountHeader: enableBssAccountHeader,
        // Include X-BSS-Account and IBM-Account-Context header in requests or not
        graphql: graphql,
        // A string represent the graphql query to be made. If it exists the query will be executed using the params, model, and acceptedParams.
        dataHelper: dataHelper // A function that will modify the response data before set it to the state
      });
    }, [setData, key, makeRequest]); // eslint-disable-line react-hooks/exhaustive-deps
    //  ^ Turn off options and params as the key will catch there changes.
    /*
       useEffect doesn't handle deep compare with objects well. There are a few options
       1. manually compare previous state with usePrevious and deep object check inside the useEffect
       2. use JSON.stringify to compare the objects.
        Since we are already encoding the options/params into the key we can use that as our comparison
    */

    // Calls Data Hook on mount if the data is already loaded, or does a reset. Does
    // cleanup on unmount.
    useEffect(function () {
      if (response && response.data && options.callback && !options.refetchOnMount) {
        options.callback(response.data);
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // If the refetchOnMount option is specified then reset the data store on mount and
    // on key change, as long as a request is not already in progress.
    useEffect(function () {
      var store = palDataHookStore[key];
      if (store && options.refetchOnMount && !store.state.loading) {
        store.reset();
      }
    }, [key, options.refetchOnMount]);
    useEffect(function () {
      return function () {
        if (palDataHookStore[key] && options.cleanupOnUnmount) {
          palDataHookStore[key].cleanup();
        }
      };
    }, [key, options.cleanupOnUnmount]);

    // Ultimately we want to return the state
    return response;
  };

  // Separate out the modifyDataHooks from the retrieve to avoid having to set keys for modify calls
  var useModifyDataHook = function useModifyDataHook() {
    var _DataHookMock$useData2;
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var shouldMockDataHook = (DataHookMock === null || DataHookMock === void 0 || (_DataHookMock$useData2 = DataHookMock.useDataHookMock) === null || _DataHookMock$useData2 === void 0 ? void 0 : _DataHookMock$useData2.call(DataHookMock)) || false;
    var _props$options2 = props.options,
      options = _props$options2 === void 0 ? {} : _props$options2;
    // Interpolates option based variables into the URI
    var uri = indexInterpolate(uriTemplate, options.templateKeys);

    // If the data hook is of type modify return a function
    return function (modifyParams, templateKeys) {
      if (Array.isArray(templateKeys)) {
        options.templateKeys = templateKeys;
      }
      return modify({
        params: modifyParams,
        // Provided Params
        options: _objectSpread2(_objectSpread2({}, options), {}, {
          mock: shouldMockDataHook || options.mock
        }),
        // Provided options
        method: method,
        // Function to call to get data
        mock: mock,
        // Mock response on return
        uri: uri,
        // URL to send the fetch request to
        headers: headers,
        // Any headers needed for the API call. content type JSON is set by default
        enableImsAccountHeader: enableImsAccountHeader,
        // Include X-IMS-Account header in request or not
        enableBssAccountHeader: enableBssAccountHeader,
        // Include X-BSS-Account and IBM-Account-Context header in requests or not
        graphql: graphql,
        // A string represent the graphql query to be made. If it exists the query will be executed using the params, model, and acceptedParams.
        dataHelper: dataHelper // A function that will modify the response data before set it to the state
      });
    };
  };
  if (type === 'modify') {
    return useModifyDataHook;
  }
  return useDataHook;
});

export { useDataHook as default };
